import { AccordionTypes, UI, HStack, VStack } from '@genial/components';
import { Spacing, Typography } from '@genial/design-tokens';
import { Utils } from '@genial/utils';
import { Box, Pressable } from 'native-base';
import React, { useMemo, useState } from 'react';

import { ExportPdfDropdown } from '../../../../shared/ExportPdfDropdown';

import { getNoticeText } from './constants';

import { ProductDistributionHeaderTypes } from '.';

export const ProductDistributionHeaderMobile: React.FC<ProductDistributionHeaderTypes.Props> = ({
  isLoading,
  products,
  totalValue,
  hasError,
  hasDateFilter = false,
  hasPdfExport = false,
  isExportingPdf = false,
  isToday,
  onDateSelected,
  onDownloadPdf,
  onSendToClient,
  selectedDate,
  principalTitle = 'Meus investimentos',
  principalTitleProps,
}) => {
  const buttonDisabled = hasError || products.length === 0 || isExportingPdf || isLoading;

  const [accordionState, setAccordionState] = useState<AccordionTypes.Status>('closed');

  const handlePress = () => {
    setAccordionState(accordionState === 'closed' ? 'opened' : 'closed');
  };

  const handleClosed = () => {
    setAccordionState('closed');
  };

  const getTotalInvested = () => {
    if (hasError) {
      return '--';
    }
    return Utils.Mask.ScaleMoney(totalValue);
  };

  const handleClearDate = () => {
    onDateSelected?.(undefined);
  };

  const handleSelectDate = (date: Date) => {
    handleClosed();
    onDateSelected?.(date);
  };

  const noticeMemoized = useMemo(
    () => (
      <>
        {!!selectedDate && (
          <UI.Notice
            testId="notice-not-today"
            type="warning"
            backgroundColor={Utils.ColorToken.WithOpacity(
              'feedback.attention.lightest',
              'opacity.medium'
            )}
            textObjects={[{ text: getNoticeText(selectedDate), variant: 'regular' }]}
          />
        )}
      </>
    ),
    [selectedDate]
  );

  return (
    <VStack space={Spacing['stack.xxxs']}>
      <HStack justifyContent="space-between">
        <UI.Text
          variant="semibold"
          color="neutral.medium"
          textTransform="uppercase"
          {...principalTitleProps}>
          {principalTitle}
        </UI.Text>
        <UI.Text variant="semibold" textTransform="uppercase">
          {getTotalInvested()}
        </UI.Text>
      </HStack>
      <HStack justifyContent="space-between">
        {hasDateFilter && (
          <UI.Accordion
            testId="invested-assets-filter-accordion"
            status={accordionState}
            onPress={handlePress}
            onClosed={handleClosed}
            hasShadow={false}>
            <UI.Accordion.Visible
              hideDefaultIcons
              shadow="2"
              borderRadius="full"
              px={Spacing['inline.xxxs']}
              py={Spacing['inline.nano']}
              containerProps={{ p: 0 }}>
              <Pressable onPress={handlePress} disabled={isExportingPdf}>
                <HStack space={Spacing['inline.quarck']} alignItems="center">
                  <UI.Text variant="semibold" fontSize={Typography['fontsize.md']}>
                    Filtrar
                  </UI.Text>
                  {!!selectedDate && (
                    <Box
                      rounded={'full'}
                      w={'20px'}
                      h={'20px'}
                      alignItems={'center'}
                      justifyContent={'center'}
                      bg={'brand.tertiary.light'}>
                      <UI.Text
                        variant="regular"
                        fontSize={Typography['fontsize.xs']}
                        color={'neutral.white'}>
                        1
                      </UI.Text>
                    </Box>
                  )}
                </HStack>
              </Pressable>
            </UI.Accordion.Visible>
            <UI.Accordion.Expanded>
              <HStack maxW="full">
                <UI.Calendar
                  multiple={false}
                  date={selectedDate}
                  onSelectDate={handleSelectDate}
                  onClear={handleClearDate}
                  w="full"
                />
              </HStack>
            </UI.Accordion.Expanded>
          </UI.Accordion>
        )}
        {hasPdfExport && (
          <HStack alignItems="center" space={Spacing['inline.xxxs']}>
            <ExportPdfDropdown
              isExporting={isExportingPdf}
              isDisabled={buttonDisabled}
              onDownload={() => onDownloadPdf?.()}
              onSendToClient={() => onSendToClient?.()}
            />
          </HStack>
        )}
      </HStack>
      {!isToday && !!selectedDate && noticeMemoized}
    </VStack>
  );
};
